import React from 'react';
import { Search, TrendingUp, Globe, Shield, Star, AlertTriangle, Info, CheckCircle } from 'lucide-react';
import {
  Button,
  Card, CardHeader, CardTitle, CardBody,
  Input,
  Pill,
  Section,
  Icon,
  IconCircle,
  ProgressBar,
  StatCallout,
  DecorativeShapes,
} from '@/components/flat';

const Divider: React.FC<{ label: string }> = ({ label }) => (
  <div className="flex items-center gap-4 my-8">
    <div className="flex-1 border-t border-gray-200" />
    <span className="text-xs font-semibold text-gray-400 uppercase tracking-widest">{label}</span>
    <div className="flex-1 border-t border-gray-200" />
  </div>
);

const DesignSystem: React.FC = () => (
  <div className="relative min-h-screen bg-white">
    <DecorativeShapes />

    <div className="relative max-w-3xl mx-auto px-6 py-16">
      <div className="mb-12">
        <h1 className="text-4xl font-bold text-gray-900 mb-2">DomBase Design System</h1>
        <p className="text-gray-500 text-lg">Flat UI primitives — zero shadows, zero gradients on UI elements.</p>
      </div>

      {/* BUTTON */}
      <Divider label="Button" />
      <div className="space-y-4">
        <div className="flex flex-wrap gap-3">
          <Button variant="primary">Primary</Button>
          <Button variant="secondary">Secondary</Button>
          <Button variant="muted">Muted</Button>
          <Button variant="outline">Outline</Button>
          <Button variant="ghost">Ghost</Button>
          <Button variant="danger">Danger</Button>
        </div>
        <div className="flex flex-wrap gap-3">
          <Button size="sm">Small</Button>
          <Button size="md">Medium</Button>
          <Button size="lg">Large</Button>
        </div>
        <div className="flex flex-wrap gap-3">
          <Button disabled>Disabled</Button>
          <Button loading>Loading</Button>
          <Button variant="primary"><Search className="h-4 w-4 mr-2" />With Icon</Button>
        </div>
      </div>

      {/* PILL */}
      <Divider label="Pill" />
      <div className="flex flex-wrap gap-3">
        <Pill variant="success">Available</Pill>
        <Pill variant="warning">Expiring</Pill>
        <Pill variant="error">Risk</Pill>
        <Pill variant="info">Trending</Pill>
        <Pill variant="muted">Unknown</Pill>
      </div>

      {/* INPUT */}
      <Divider label="Input" />
      <div className="space-y-4 max-w-sm">
        <Input label="Keyword" placeholder="Enter a keyword (e.g., startup)" />
        <Input label="Domain" placeholder="example.com" />
        <Input label="Error State" placeholder="bad input" error hint="This field is required" />
        <Input placeholder="No label" />
      </div>

      {/* PROGRESS BAR */}
      <Divider label="ProgressBar" />
      <div className="space-y-4 max-w-sm">
        <ProgressBar value={72} color="primary" label="Domain Score" showValue />
        <ProgressBar value={55} color="secondary" label="Availability" showValue />
        <ProgressBar value={38} color="accent" label="Risk Level" showValue />
        <ProgressBar value={15} color="muted" label="Completeness" showValue />
      </div>

      {/* ICON + ICON CIRCLE */}
      <Divider label="Icon / IconCircle" />
      <div className="flex flex-wrap gap-4">
        <Icon icon={Search} size="sm" />
        <Icon icon={Globe} size="md" />
        <Icon icon={TrendingUp} size="lg" />
      </div>
      <div className="flex flex-wrap gap-3 mt-4">
        <IconCircle icon={Search} color="primary" size="sm" />
        <IconCircle icon={Shield} color="secondary" size="md" />
        <IconCircle icon={Star} color="accent" size="lg" />
        <IconCircle icon={AlertTriangle} color="muted" size="md" />
      </div>

      {/* STAT CALLOUT */}
      <Divider label="StatCallout" />
      <div className="grid grid-cols-2 gap-3">
        <StatCallout icon={Globe} iconColor="primary" label="Domains Scanned" value="12,450" sub="Last 30 days" />
        <StatCallout icon={CheckCircle} iconColor="secondary" label="Available Domains" value="3,872" />
        <StatCallout icon={TrendingUp} iconColor="accent" label="Top Keyword" value="startup" sub="#1 this week" />
        <StatCallout icon={Info} iconColor="muted" label="Avg. Domain Score" value="67 / 100" />
      </div>

      {/* CARD */}
      <Divider label="Card" />
      <div className="space-y-4">
        <Card>
          <CardHeader>
            <CardTitle>Default Card</CardTitle>
          </CardHeader>
          <CardBody>
            Standard card with 24px padding, 1px border, no shadow.
          </CardBody>
        </Card>
        <Card flush className="p-0 overflow-hidden">
          <div className="bg-blue-500 h-2 w-full" />
          <div className="p-6">
            <CardTitle>Flush Card</CardTitle>
            <CardBody className="mt-2">Flush removes built-in padding for custom layouts.</CardBody>
          </div>
        </Card>
      </div>

      {/* SECTION */}
      <Divider label="Section" />
      <Section title="Section Title" subtitle="Optional subtitle for context." tight className="bg-gray-50 rounded-lg border border-gray-200 p-8">
        <p className="text-gray-600 text-sm text-center">Section body content lives here.</p>
      </Section>

      {/* DECORATIVE SHAPES */}
      <Divider label="DecorativeShapes" />
      <div className="relative h-40 bg-gray-50 rounded-lg border border-gray-200 overflow-hidden flex items-center justify-center">
        <DecorativeShapes />
        <span className="relative text-gray-500 text-sm">Background-only decoration — no UI elements</span>
      </div>

      {/* TYPOGRAPHY */}
      <Divider label="Typography" />
      <div className="space-y-3">
        <h1 className="text-4xl font-bold text-gray-900">Heading 1</h1>
        <h2 className="text-3xl font-bold text-gray-900">Heading 2</h2>
        <h3 className="text-2xl font-semibold text-gray-900">Heading 3</h3>
        <p className="text-base text-gray-700">Body — Outfit Variable, 16px, gray-700.</p>
        <p className="text-sm text-gray-500">Small — 14px, gray-500.</p>
        <p className="text-xs text-gray-400">XSmall / caption — 12px, gray-400.</p>
        <p className="text-sm font-semibold text-blue-500">Link / Primary text.</p>
      </div>

      {/* COLOR PALETTE */}
      <Divider label="Color Palette" />
      <div className="grid grid-cols-3 gap-3">
        {[
          { label: 'Primary', cls: 'bg-blue-500' },
          { label: 'Secondary', cls: 'bg-emerald-500' },
          { label: 'Accent', cls: 'bg-amber-500' },
          { label: 'FG', cls: 'bg-gray-900' },
          { label: 'Muted', cls: 'bg-gray-100 border border-gray-200' },
          { label: 'Border', cls: 'bg-gray-200' },
        ].map(({ label, cls }) => (
          <div key={label} className="flex flex-col items-center gap-2">
            <div className={`w-full h-12 rounded-md ${cls}`} />
            <span className="text-xs text-gray-500">{label}</span>
          </div>
        ))}
      </div>
    </div>
  </div>
);

export default DesignSystem;
